if (!customElements.get('laye-emblem-builder')) {
  customElements.define('laye-emblem-builder', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.labels = JSON.parse(this.querySelector('[data-emblem-labels]').textContent);
      this.fonts = JSON.parse(this.querySelector('[data-emblem-fonts]').textContent);
      this.fontLoads = new Map();
      this.mode = this.querySelector('[data-emblem-mode]');
      this.text = this.querySelector('[data-emblem-text]');
      this.file = this.querySelector('[data-artwork-file]');
      this.canvas = this.querySelector('canvas');
      this.idea = this.querySelector('[data-ai-idea]');
      this.prompt = this.querySelector('[data-ai-prompt]');
      this.status = this.querySelector('[data-emblem-status]');
      this.addEventListener('input', () => this.update());
      this.addEventListener('change', () => this.update());
      this.file.addEventListener('change', () => this.previewUpload());
      this.querySelector('[data-emblem-download]').addEventListener('click', async () => {
        if (!this.text.reportValidity()) return;
        try { await this.loadSelectedFont(); this.draw(); const link = document.createElement('a'); link.href = this.canvas.toDataURL('image/png'); link.download = 'laye-emblem-mockup.png'; document.body.appendChild(link); link.click(); link.remove(); }
        catch { this.status.textContent = this.labels.renderError; }
      });
      this.querySelector('[data-copy-prompt]').addEventListener('click', async () => {
        this.update();
        try { await navigator.clipboard.writeText(this.prompt.value); this.querySelector('[data-ai-status]').textContent = this.labels.copied; }
        catch { this.prompt.focus(); this.prompt.select(); this.querySelector('[data-ai-status]').textContent = this.labels.copyFallback; }
      });
      this.update();
    }
    update() {
      const textMode = this.mode.value === 'Text mockup';
      const aiMode = this.mode.value === 'AI artwork via ChatGPT';
      this.querySelector('[data-text-fields]').hidden = !textMode;
      this.querySelector('[data-ai-fields]').hidden = !aiMode;
      this.querySelector('[data-upload-fields]').hidden = textMode;
      this.querySelectorAll('[data-text-fields] input, [data-text-fields] select').forEach(el => el.disabled = !textMode);
      this.text.required = textMode;
      this.file.disabled = textMode;
      this.file.required = !textMode;
      this.idea.disabled = !aiMode;
      this.idea.required = aiMode;
      this.file.setCustomValidity(!textMode && this.file.files[0] && !this.validFile(this.file.files[0]) ? this.labels.fileError : '');
      this.prompt.value = this.labels.prefix + (this.text.value.trim() || '(use the wording in the design idea)') + this.labels.suffix + this.idea.value.trim();
      this.draw();
      this.loadSelectedFont().then(() => this.draw()).catch(() => { this.status.textContent = this.labels.fontError; });
    }
    async loadSelectedFont() {
      const selected = this.querySelector('[name="properties[Font style]"]:checked').value;
      const family = this.fonts[selected].family;
      if (!this.fontLoads.has(family)) {
        const loading = Promise.race([
          document.fonts.load('170px "' + family + '"').then(faces => { if (!faces.length) throw new Error(this.labels.fontError); }),
          new Promise((_, reject) => setTimeout(() => reject(new Error(this.labels.fontError)), 10000))
        ]);
        this.fontLoads.set(family, loading);
        loading.catch(() => this.fontLoads.delete(family));
      }
      await this.fontLoads.get(family);
    }
    validFile(file) { return /\.(png|jpe?g|webp|pdf)$/i.test(file.name) && file.size > 0 && file.size <= 10 * 1024 * 1024; }
    previewUpload() {
      if (this.imageURL) URL.revokeObjectURL(this.imageURL);
      const image = this.querySelector('[data-upload-preview]');
      const file = this.file.files[0];
      image.hidden = true;
      image.removeAttribute('src');
      this.querySelector('[data-upload-status]').textContent = file ? (this.validFile(file) ? file.name : this.labels.fileError) : '';
      if (file && this.validFile(file) && /\.(png|jpe?g|webp)$/i.test(file.name)) {
        this.imageURL = URL.createObjectURL(file); image.src = this.imageURL; image.hidden = false;
      }
    }
    draw() {
      const ctx = this.canvas.getContext('2d');
      const font = this.querySelector('[name="properties[Font style]"]:checked')?.value || 'Racing Sans One';
      const color = this.querySelector('[data-emblem-color]').value;
      const spacing = this.querySelector('[data-emblem-spacing]').value;
      const text = this.text.value.trim() || 'YOUR TEXT';
      ctx.clearRect(0, 0, 1600, 600);
      ctx.fillStyle = '#15161b'; ctx.fillRect(0, 0, 1600, 600);
      const gradient = ctx.createRadialGradient(800, 300, 0, 800, 300, 800);
      gradient.addColorStop(0, '#292b35'); gradient.addColorStop(1, '#101116');
      ctx.fillStyle = gradient; ctx.fillRect(0, 0, 1600, 600);
      ctx.font = '170px "' + this.fonts[font].family + '", sans-serif'; ctx.textBaseline = 'middle';
      if ('letterSpacing' in ctx) ctx.letterSpacing = spacing === 'Wide' ? '12px' : spacing === 'Tight' ? '-3px' : '0px';
      const measured = Math.max(ctx.measureText(text).width, 1);
      const scale = Math.min(1, 1420 / measured);
      ctx.save(); ctx.translate(800, 300); ctx.scale(scale, scale); ctx.textAlign = 'center';
      ctx.shadowColor = color; ctx.shadowBlur = 35;
      ctx.strokeStyle = color; ctx.lineWidth = 4;
      ctx.strokeText(text, 0, 0);
      ctx.shadowBlur = 15; ctx.fillStyle = '#fbfaff'; ctx.fillText(text, 0, 0);
      ctx.restore();
    }
    async renderBlob() {
      await this.loadSelectedFont();
      this.draw();
      return new Promise((resolve, reject) => this.canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error(this.labels.renderError)), 'image/png'));
    }
    async prepareArtwork() {
      this.mockupFile = null;
      this.status.textContent = this.labels.working;
      if (this.mode.value === 'Text mockup') {
        const blob = await this.renderBlob();
        this.mockupFile = new File([blob], 'laye-emblem-text-mockup.png', { type: 'image/png' });
      } else if (!this.file.files[0] || !this.validFile(this.file.files[0])) {
        this.status.textContent = this.labels.fileError;
        throw new Error(this.labels.fileError);
      }
      this.status.textContent = this.labels.ready;
    }
    disconnectedCallback() { if (this.imageURL) URL.revokeObjectURL(this.imageURL); }
  });
}
